import { IBusiness } from '../entities/businessBase';
import { ICalendar } from '@/coreDomainsModule/date';
import { TableType } from '../../coreDomainsModule/table';
import { simpleCopy } from '@/utils/deepCopy';

/**
 *　filterFunctionのクラス
 */
type filterFunction = (business: IBusiness) => boolean;
/**
 * Functionの型
 */
type FunctionType = [(args: [IBusiness[], filterFunction]) => IBusiness[], ...((businesses: IBusiness[]) => IBusiness[])[]];

/**
 * 取引を扱うサービス
 */
export class BusinessService {
  /**
   * 1層目のみ
   * (args: [IBusiness[], filterFunction])であり、二層目以降は((businesses: IBusiness[]) => IBusiness[])でバケツリレー
   * @param targetBusinesses - 取引情報
   * @param tableType - テーブルの種類
   * @param recommendFilter -
   * @param filterFunction - フィルター関数
   * @param calendar - カレンダー
   * @param now - 現在時刻
   */
  public static filterBusinesses = async (
    targetBusinesses: IBusiness[],
    tableType?: string,
    recommendFilter?: boolean,
    filterFunction?: filterFunction,
    calendar?: ICalendar,
    now = new Date()
  ): Promise<IBusiness[]> => {
    const copyBusinesses = Object.assign([], targetBusinesses);
    const functions: FunctionType = [
      (args: [IBusiness[], filterFunction]) => {
        return args[0];
      },
    ];
    if (filterFunction) {
      /**
       * filterFunction
       * @param args - 引数
       */
      functions[0] = function (args: [IBusiness[], filterFunction]) {
        let businesses = args[0];
        const filterFunction = args[1];
        businesses = businesses.filter((p) => filterFunction(p));
        return businesses;
      };
    }
    if (!recommendFilter) {
      // TODO: recommendFilter=falseの時にフィルターすることになっている？直感的には逆では？
      functions.push(function (businesses: IBusiness[]) {
        return businesses.filter((p) => {
          if (p.recommend && p.recommend.length > 0 && p.recommend[0].year > 0) {
            const getDate = new Date(p.recommend[0].year, p.recommend[0].month - 1, p.recommend[0].day);
            return getDate.getTime() <= now.getTime();
          } else {
            return false;
          }
        });
      });
    }
    // 出力層
    functions.push(function (businesses: IBusiness[]) {
      return businesses;
    });
    return functions
      .reduce(
        (pre: any /* ここany外せる方外してください */, forward) => pre.then(forward),
        Promise.resolve([copyBusinesses, filterFunction])
      )
      .then((res: IBusiness[]) => {
        // asyncを許容したいかつ、then().then()....と今後なる可能性をひめているため、
        // 先に関数の合成の下地を作っておく
        // これ嫌いな人がいれば上からawaitでも良いです(ただawaitするといずれ、このfilterBusinessesがsyncにしたい時にめんどくさそうなので...)
        targetBusinesses = res;
        return res;
      })
      .catch((err) => console.log('err', err));
    // key
    // console.log('business', res)
    // state.businesses = res;
  };

  /**
   * 取引情報を取得する
   * @param tableType -
   */
  public static generateFilterFunction = (tableType?: TableType): filterFunction | undefined => {
    // const output: {[key: string]: boolean}[] = []
    let output: filterFunction | undefined = undefined;
    switch (tableType) {
      case 'registration':
        /**
         * output
         * @param business - 取引情報
         */
        output = function (business) {
          return !business.isTrashed;
        };
        break;
      case 'recommend-order':
        /**
         * output
         * @param business - 取引情報
         */
        output = function (business) {
          return !business.isTrashed; //確定発注を表示しないとかはなくなる？
        };
        break;
      case 'fix-order':
        /**
         * output
         * @param business - 取引情報
         */
        output = function (business) {
          return true;
        };
        break;
      case 'forecast':
        /**
         * output
         * @param business - 取引情報
         */
        output = function (business) {
          return !business.isTrashed; //確定発注を表示しないとかはなくなる？
        };
        break;
      case 'bom-parent':
        /**
         * output
         * @param business - 取引情報
         */
        output = function (business) {
          return !business.isTrashed;
        };
        break;
      case 'bom-parts':
        // 買い手取引のみを表示する
        /**
         * output
         * @param business - 取引情報
         */
        output = function (business) {
          return !business.isTrashed;
        };
        break;
      case 'inventory':
        /**
         * output
         * @param business - 取引情報
         */
        output = function (business) {
          return !business.isTrashed; //確定発注を表示しないとかはなくなる？
        };
        break;
      case 'trash':
        /**
         * output
         * @param business - 取引情報
         */
        output = function (business) {
          return !!business.isTrashed; //確定発注を表示しないとかはなくなる？
        };
        break;
      case 'organization-business':
        /**
         * output
         * @param business - 取引情報
         */
        output = function (business) {
          return !business.isTrashed;
        };
        break;
      case 'rejected-business':
        // 却下の取引のみを表示する
        /**
         * output
         * @param business - 取引情報
         */
        output = function (business) {
          return !business.isTrashed;
        };
        break;
      case 'receive-cooperation':
        // 連携依頼受信・訂正依頼受信・削除依頼受信のみを表示する(現状はIBusinessで管理しているので取引テーブルができたら対応する)
        /**
         * output
         * @param business - 取引情報
         */
        output = function (business) {
          return !business.isTrashed;
        };
        break;
      case 'unsupported':
        // 未対応の取引のみを表示する
        /**
         * output
         * @param business - 取引情報
         */
        output = function (business) {
          return !business.isTrashed;
        };
        break;
      case 'send-cooperation':
        // 未対応と連携依頼送付済みの取引のみを算出する
        /**
         * output
         * @param business - 取引情報
         */
        output = function (business) {
          return !business.isTrashed;
        };
        break;
      case 'bom':
        // bomDataを持っているIBusinessのみを表示する
        /**
         * output
         * @param business - 取引情報
         */
        output = function (business) {
          // return !business.isTrashed;
          if (business != undefined) {
            return true;
          } else {
            return false;
          }
        };
        break;
      case 'receive-forecast': //受領内示(自分がsellerの時の取引に限定される)
        /**
         * TODO: 220912 上の条件でバグが発生していたので一旦切っています（もう少しちゃんと考える必要あり）
         * @param business - 取引情報
         */
        output = function (business) {
          if (!business.isTrashed) {
            return true;
          } else {
            return false;
          }
        };
        break;
      case 'send-forecast':
        /**
         * 確定発注を表示しないとかはなくなる？
         * @param business - 取引情報
         */
        output = function (business) {
          return !business.isTrashed;
        };
        break;
      case 'baseTable':
        /**
         * 取引一覧では一旦、仕入先内示と確定発注のどちらも未対応の取引のみを表示するようにしている
         * @param business - 取引情報
         */
        output = function (business) {
          if (business.isTrashed) {
            return false;
          }
          return true;
        };
        break;
      case 'supplierTable':
        /**
         * 仕入先内示一覧では、仕入先内示へ追加済みの取引のみを表示する
         * @param business - 取引情報
         */
        output = function (business) {
          if (business.isTrashed) {
            return false;
          }
          // 仕入先内示への対応が完了している取引を表示
          return business.businessForecastState === 'supplierForecastCompleted' || business.businessForecastState === 'completed';
        };
        break;
      case 'firmOrderTable':
        /**
         * 確定発注一覧では、確定発注を登録済みの取引のみを表示する
         * 確定発注への対応が完了している取引を表示
         * TODO: 表示確認用で仕入先内示の出力を行なっている。確定発注登録処理実装後は↑に変更する
         * @param business - 取引情報
         */
        output = function (business) {
          if (business.isTrashed) {
            return false;
          }
          return business.businessForecastState === 'orderCompleted' || business.businessForecastState === 'completed';
        };
        break;
      case 'unmanagedBusinessTypeAnnounce':
      case 'unmanagedBusinessTypeSalesResult':
      case 'unmanagedBusinessTypeInventory':
      case 'unmanagedBusinessTypeArrival':
        /**
         * 未管理取引一覧では、未管理取引のみを表示する
         * @param business - 取引情報
         */
        output = function (business) {
          return !business.isTrashed;
        };
        break;
      default:
    }
    return output;
  };

  /**
   * 取引情報からrecommendの情報を分離し、分離した単一の発注情報を持ったIBusinessオブジェクトの作成を行う
   * @param businesses - 取引情報
   */
  public static getFirmOrder(businesses: IBusiness[]) {
    const resultBusinesses: IBusiness[] = [];
    // 取引情報からrecommendの情報を分離し、分離した単一の発注情報を持ったIBusinessオブジェクトの作成を行う
    businesses.forEach((business) => {
      if (business.recommend && business.recommend.length !== 0) {
        business.recommend.forEach((ent) => {
          const useBusinessInfo: IBusiness = simpleCopy(business);
          // 取引IDと発注IDを結合することで、取引情報と発注情報の関係性を残したい
          useBusinessInfo.businessId = business.businessId + '-' + ent.arrivalId;
          useBusinessInfo.recommend = [ent];
          resultBusinesses.push(useBusinessInfo);
        });
      }
    });
    return resultBusinesses;
  }
}
